import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import ExplorerColumnsTable, {
  type ExplorerColumnRow,
} from "@/features/adp/components/source/dialogs/explorer-dialog/components/ExplorerColumnsTable";
import { useFinalizeRegisteredSourceMutation } from "@/features/adp/hooks/useFinalizeRegisteredSourceMutation";
import { useCreateSourceFileAndFieldsMutation } from "@/features/adp/mutations/useSourceMutations";
import { useNotRegisteredTablesQuery } from "@/features/adp/queries/useSourceQueries";
import { useGetColumnsQuery } from "@/features/adp/queries/useConnectionQueries";
import type {
  NotRegisteredTableDatabase,
  SourceFileFieldPayloadItem,
  SourceFilePayloadItem,
} from "@/features/adp/services/source/source.types";
import type { BaseDatabaseConfig } from "@/features/adp/services/common/common.types";
import { closeOverlay, openOverlay, type AddNewTableOverlayData } from "@/features/adp/state/overlaySlice";
import { DATA_TYPES, DataTypes } from "@/features/adp/types/data.types";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Input } from "@/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Skeleton } from "@/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Info, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import styles from "./AddNewTable.module.scss";

function normalizeDataType(value: string | undefined | null): string {
  const normalized = (value ?? '').trim().toLowerCase();
  return DATA_TYPES.includes(normalized as DataTypes) ? normalized : DataTypes.string;
}

function isAddNewTableOverlayData(value: unknown): value is AddNewTableOverlayData {
  return Boolean(value && typeof value === "object" && "sourceDataSourceId" in value);
}

/**
 * Build the per-database payload for POST `/connections/columns`.
 * Mirrors the Angular `showdatatypes/v2` body so the backend whitelist accepts
 * it — empty-string defaults for `table_path` / `date_format` / `file_delimiter`
 * are required even when the source is a regular RDBMS.
 */
function buildColumnsPayloadDatabase(
  connection: NotRegisteredTableDatabase,
  tableName: string,
): BaseDatabaseConfig & Record<string, unknown> {
  const payload: BaseDatabaseConfig & Record<string, unknown> = {
    type: connection.type,
    source_name: connection.source_name,
    username: connection.username ?? '',
    password: connection.password ?? '',
    host: connection.host ?? '',
    port: connection.port ?? null,
    database: connection.database ?? '',
    schema: connection.schema ?? '',
    secret_key: Array.isArray(connection.secret_key)
      ? connection.secret_key.join(',')
      : (connection.secret_key ?? ''),
    table_path: '',
    table_name: tableName,
    date_format: '',
    file_delimiter: '',
  };
  return payload;
}

const AddNewTable = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const finalizeRegisteredSource = useFinalizeRegisteredSourceMutation();
  const { isOpen, type, data } = useAppSelector((state) => state.overlay);

  const isVisible = isOpen && type === "source-add-new-table";
  const modalData = isVisible && isAddNewTableOverlayData(data) ? data : null;

  const [tableSearch, setTableSearch] = useState("");
  const [selectedTableName, setSelectedTableName] = useState<string>("");
  const [isTableSelectOpen, setIsTableSelectOpen] = useState(false);
  const [rows, setRows] = useState<ExplorerColumnRow[]>([]);

  const sourceDataSourceId = modalData?.sourceDataSourceId ?? 0;
  const sourceInstance = modalData?.sourceInstance ?? "";

  const notRegisteredTablesQuery = useNotRegisteredTablesQuery(
    isVisible
      ? { source_data_source_id: sourceDataSourceId, type: sourceInstance }
      : null,
  );

  const connection = notRegisteredTablesQuery.data?.databases?.[0] ?? null;

  const columnsPayload = useMemo(() => {
    if (!isVisible || !selectedTableName || !connection) return null;
    return { databases: [buildColumnsPayloadDatabase(connection, selectedTableName)] };
  }, [connection, isVisible, selectedTableName]);

  const columnsQuery = useGetColumnsQuery(
    columnsPayload ?? { databases: [] },
    { enabled: Boolean(columnsPayload) },
  );

  const createSourceFileAndFieldsMutation = useCreateSourceFileAndFieldsMutation();

  const dataTypeOptions = useMemo(() => Object.values(DataTypes), []);

  useEffect(() => {
    if (isVisible) {
      setTableSearch("");
      setSelectedTableName("");
      setIsTableSelectOpen(false);
      setRows([]);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!columnsQuery.isSuccess) return;
    const nextRows: ExplorerColumnRow[] = columnsQuery.data.map((column) => ({
      id: column.column_name,
      selected: true,
      columnName: column.column_name,
      dataType: normalizeDataType(column.data_type),
      primaryKey: false,
      dqChecks: [],
    }));
    setRows(nextRows);
  }, [columnsQuery.data, columnsQuery.isSuccess]);

  const filteredTables = useMemo(() => {
    const list = notRegisteredTablesQuery.data?.tables ?? [];
    const search = tableSearch.trim().toLowerCase();
    if (!search) return list;
    return list.filter((tableName) => tableName.toLowerCase().includes(search));
  }, [notRegisteredTablesQuery.data, tableSearch]);

  const handleBackToList = () => {
    if (!modalData?.parentOverlay) {
      dispatch(closeOverlay());
      return;
    }
    dispatch(
      openOverlay({
        type: "source-registered-tables-list",
        data: modalData.parentOverlay,
      }),
    );
  };

  const handleClose = () => dispatch(closeOverlay());

  const updateRow = (rowId: string, patch: Partial<ExplorerColumnRow>) => {
    setRows((current) => current.map((row) => (row.id === rowId ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    if (!modalData || !selectedTableName || !modalData.nodeId) return;

    const selectedRows = rows.filter((row) => row.selected);
    if (selectedRows.length === 0) {
      toast.warning("Select at least one column to continue.");
      return;
    }

    const sourceFile: SourceFilePayloadItem[] = [
      {
        data_source_name: modalData.sourceName ?? "",
        data_source_instance: modalData.sourceInstance,
        file_name: selectedTableName,
        source_file_format: modalData.dataSourceType ?? "DATABASE",
        source_file_type: "table",
        authentication_method: "",
        payload_query: "",
        headers: "",
        request_type: "",
        params: "",
        path: "",
        file_delimiter: "",
      },
    ];

    const sourceFileField: SourceFileFieldPayloadItem[] = selectedRows.map((row) => ({
      file_name: selectedTableName,
      field_name: row.columnName,
      field_data_type: row.dataType,
      field_structure: "",
      field_size: row.dataType === DataTypes.string ? "1024" : "0",
      precision: 0,
      is_business_primary_key: row.primaryKey,
      dq_check: row.dqChecks.map((check) => check.value).join(", "),
    }));

    const createdFields = await createSourceFileAndFieldsMutation.mutateAsync({
      source_file: sourceFile,
      source_file_field: sourceFileField,
    });

    // Reuse the same connection block we send to `/connections/columns` so
    // ExplorerDialog (gear icon → revisit) can refetch columns when the user
    // switches the selected table later. ExplorerDialog overwrites `table_name`
    // with the user's current selection, so leaving the original in is harmless.
    const submittedSourcePayload = connection
      ? buildColumnsPayloadDatabase(connection, selectedTableName)
      : null;

    await finalizeRegisteredSource.mutateAsync({
      nodeId: modalData.nodeId,
      sourceDataSourceId: modalData.sourceDataSourceId,
      rooId: modalData.parentOverlay?.rooId ?? null,
      sourceName: modalData.sourceName ?? null,
      tableName: selectedTableName,
      sourceFiles: createdFields,
      registeredSourceValues: modalData.parentOverlay?.registeredSourceValues,
      submittedSourcePayload,
    });

    await queryClient.invalidateQueries({ queryKey: ["registered-source-files", sourceDataSourceId] });
    await queryClient.invalidateQueries({ queryKey: ["not-registered-tables", sourceDataSourceId] });

    toast.success(`"${selectedTableName}" added to source`);
  };

  const connectionLabel = modalData?.sourceName ?? "—";
  const hasSelectedTable = selectedTableName.length > 0;
  const hasSelectedRow = rows.some((row) => row.selected);

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        // Without a height bound the dialog grows past the viewport and the
        // footer (Save button) drops below the fold — user had to browser-zoom
        // out to reach it. Cap height + flex-col so DialogBody scrolls and the
        // footer stays pinned. `h-[80dvh]` only when a table is selected (the
        // columns table makes the content tall); short otherwise. Mirrors
        // ExplorerDialog.
        className={`max-w-4xl p-0 max-h-[80dvh] flex flex-col ${
          hasSelectedTable ? "h-[80dvh]" : "h-auto"
        }`}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className={styles.header}>
          <DialogTitle className="sr-only">Add New Table</DialogTitle>
          <div className={styles.breadcrumb}>
            <Button
              type="button"
              variant="text"
              className={styles.backButton}
              onClick={handleBackToList}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <span className={styles.breadcrumbMuted}>Source Details</span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbMuted}>Table List</span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbCurrent}>Add New Table</span>
          </div>
        </DialogHeader>

        <DialogBody className={styles.body}>
          <div className={styles.topBar}>
            <Button
              type="button"
              variant="outline"
              className={styles.backInlineButton}
              onClick={handleBackToList}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <h2 className={styles.title}>Add New Table</h2>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {modalData?.sourceName ? `${modalData.sourceName} Connection` : "Connection"}
            </label>
            <Input value={connectionLabel} readOnly className={styles.readOnlyInput} />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Table Name
              <Info className={styles.infoIcon} />
            </label>
            <Select
              value={selectedTableName}
              onValueChange={(value) => {
                setSelectedTableName(value);
                setIsTableSelectOpen(false);
              }}
              open={isTableSelectOpen}
              onOpenChange={setIsTableSelectOpen}
            >
              <SelectTrigger className={styles.tableSelect} iconName="unfold_more">
                <SelectValue placeholder="Select Table Name" />
              </SelectTrigger>
              <SelectContent className={styles.tableSelectContent}>
                <div className={styles.tableSearchWrap}>
                  <Search className={styles.tableSearchIcon} />
                  <Input
                    value={tableSearch}
                    onChange={(event) => setTableSearch(event.target.value)}
                    placeholder="Search Item..."
                    className={styles.tableSearchInput}
                    // This input lives inside Radix <SelectContent>, which runs
                    // its own keyboard typeahead on the content. Without this,
                    // every keystroke is captured by Radix and focus snaps back
                    // to the listbox — the user has to re-click for each char.
                    // Stop the events from reaching Radix's content handler.
                    onKeyDown={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                  />
                </div>
                {notRegisteredTablesQuery.isPending ? (
                  <div className={styles.tableSelectStatus}>Loading…</div>
                ) : filteredTables.length === 0 ? (
                  <div className={styles.tableSelectStatus}>No tables available</div>
                ) : (
                  filteredTables.map((tableName) => (
                    <SelectItem key={tableName} value={tableName}>
                      {tableName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {hasSelectedTable && (
            <div className={styles.columnsSection}>
              <p className={styles.columnsLabel}>Which columns would you like to choose?</p>

              {columnsQuery.isPending && (
                <div className={styles.skeletonStack}>
                  {[0, 1, 2, 3].map((skeletonIndex) => (
                    <Skeleton key={skeletonIndex} className={styles.rowSkeleton} />
                  ))}
                </div>
              )}

              {!columnsQuery.isPending && columnsQuery.isError && (
                <p className={styles.errorState}>
                  {columnsQuery.error?.message || "Failed to load columns."}
                </p>
              )}

              {!columnsQuery.isPending && columnsQuery.isSuccess && rows.length === 0 && (
                <p className={styles.emptyState}>No columns returned for this table.</p>
              )}

              {!columnsQuery.isPending && columnsQuery.isSuccess && rows.length > 0 && (
                <ExplorerColumnsTable
                  rows={rows}
                  dataTypeOptions={dataTypeOptions}
                  onToggleAll={(checked) =>
                    setRows((current) => current.map((row) => ({ ...row, selected: checked })))
                  }
                  onToggleRow={(rowId, checked) => updateRow(rowId, { selected: checked })}
                  onTogglePrimaryKey={(rowId, checked) => updateRow(rowId, { primaryKey: checked })}
                  onDataTypeChange={(rowId, value) => updateRow(rowId, { dataType: value })}
                  onDqChecksChange={(rowId, value) => updateRow(rowId, { dqChecks: value })}
                />
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter className={styles.footer}>
          <Button
            type="button"
            className={styles.saveButton}
            disabled={
              !hasSelectedTable ||
              createSourceFileAndFieldsMutation.isPending ||
              finalizeRegisteredSource.isPending ||
              !hasSelectedRow
            }
            onClick={handleSave}
          >
            {createSourceFileAndFieldsMutation.isPending || finalizeRegisteredSource.isPending
              ? "Saving..."
              : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewTable;
